import type { CatalogGift, Gift, GiftCatalog, GiftKind, GiftTier } from '@/api/types';
import { ApiError } from '@/api/errors';
import { GIFT_IDS, GIFT_VISUALS, type GiftId } from '@/features/gifts/fx';

/**
 * Gifts (SPEC_V8 §3–§6): pure client rules. The server is the source of truth
 * for ids, prices, kinds and every send rule; the fx module owns the visuals,
 * keyed by gift id.
 */

export const TIERS: { id: GiftTier; label: string }[] = [
  { id: 'little', label: 'Little' },
  { id: 'sweet', label: 'Sweet' },
  { id: 'wow', label: 'Wow' },
];

export function isGiftId(id: string): id is GiftId {
  return (GIFT_IDS as readonly string[]).includes(id);
}

/** Display name: the server catalog wins, then the fx module's, then the id. */
export function giftName(id: string, catalog?: GiftCatalog | null): string {
  const c = catalog?.gifts.find((g) => g.id === id);
  if (c) return c.name;
  return isGiftId(id) ? GIFT_VISUALS[id].name : id.replace(/_/g, ' ');
}

export function giftEmoji(id: string): string {
  return isGiftId(id) ? GIFT_VISUALS[id].emoji : '🎁';
}

/** "a flower crown", "an oud" — lower-case names after "a/an" in sentences. */
export function aGift(name: string): string {
  const n = name.toLowerCase();
  return `${/^[aeiou]/.test(n) ? 'an' : 'a'} ${n}`;
}

/** Gifts of a tray tab, in catalog (price) order. Chat offers card gifts only. */
export function giftsFor(catalog: GiftCatalog | null | undefined, tier: GiftTier | null, ctx: 'call' | 'chat'): CatalogGift[] {
  return (catalog?.gifts ?? []).filter((g) => isGiftId(g.id) && g.where.includes(ctx) && (ctx === 'call' ? g.tier === tier : g.kind === 'card'));
}

/** Why a gift can't be sent right now (shown on the tile), else null. */
export type DisabledReason = 'coins' | 'gifts_off' | 'not_live' | 'safety' | 'locked' | 'cooldown' | 'moment_pending';

export const DISABLED_TEXT: Record<DisabledReason, string> = {
  coins: 'Not enough coins',
  gifts_off: 'They aren’t accepting gifts',
  not_live: 'Gifts work once you’re both here',
  safety: 'Gifts are paused in this call',
  locked: 'Your wallet is locked',
  cooldown: 'Give that one a moment',
  moment_pending: 'Waiting for their answer',
};

export interface TrayState {
  balance: number | null;
  locked: boolean;
  /** Call-level reasons that disable every gift (server answers remembered for this call). */
  blocked: DisabledReason | null;
  momentPending: boolean;
  coolingDown: boolean;
}

export function disabledReason(g: CatalogGift, s: TrayState): DisabledReason | null {
  if (s.blocked) return s.blocked;
  if (s.locked) return 'locked';
  if (s.coolingDown) return 'cooldown';
  if (g.kind === 'moment' && s.momentPending) return 'moment_pending';
  if (s.balance !== null && s.balance < g.coins) return 'coins';
  return null;
}

/** Map a refused send (409/429) to a call-level reason the tray remembers, else null. */
export function reasonFromError(e: unknown): DisabledReason | null {
  if (!(e instanceof ApiError)) return null;
  switch (e.details.reason) {
    case 'gifts_off':
    case 'unavailable':
      return 'gifts_off';
    case 'call_not_live':
      return 'not_live';
    case 'safety':
    case 'reported':
      return 'safety';
    case 'wallet_locked':
      return 'locked';
    case 'gift_cooldown':
    case 'ar_cooldown':
      return 'cooldown';
    case 'moment_pending':
      return 'moment_pending';
    case 'insufficient_coins':
      return 'coins';
  }
  return null;
}

/** Calm copy for a refused send. */
export function sendErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    const r = e.details.reason;
    if (r === 'daily_spend_cap' || r === 'recipient_cap') return 'That’s the gift limit for today. It resets at midnight UTC.';
    if (r === 'ar_cooldown') return 'Give their last face gift a few seconds first.';
    const reason = reasonFromError(e);
    if (reason) return DISABLED_TEXT[reason] + '.';
    if (e.status === 0) return 'You’re offline. Try again in a moment.';
    return e.message;
  }
  return 'Something went wrong. Try again.';
}

/** Which tile an effect decorates (SPEC_V8 §3, §5). */
export type FxTarget = 'remote' | 'self' | 'stage';

/**
 * `ar` sits on the recipient's face: the remote tile for the sender, the
 * self-view (mirrored) for the recipient. Scenes play over both tiles.
 * Cards settle on the recipient's side for the sender and play big on the
 * stage for the recipient. Moments are the Photo booth (no overlay).
 */
export function fxTarget(g: Pick<Gift, 'to'> & { effect: { kind: GiftKind } | null }, myId: string): FxTarget | null {
  const kind = g.effect?.kind;
  if (!kind || kind === 'moment') return null;
  const mine = g.to === myId;
  if (kind === 'scene') return 'stage';
  if (kind === 'ar') return mine ? 'self' : 'remote';
  return mine ? 'stage' : 'remote';
}

/**
 * Fold a delivered gift into the active effects: deduplicated by id, and one
 * active `ar` effect per recipient (a newer one replaces it).
 */
export function addEffect(effects: Gift[], g: Gift): Gift[] {
  if (!g.effect || g.effect.kind === 'moment' || g.status !== 'delivered' || g.removed) return effects;
  const rest = effects.filter((x) => x.id !== g.id && !(g.effect!.kind === 'ar' && x.effect?.kind === 'ar' && x.to === g.to));
  return [...rest, g];
}

/** Effect start on the client clock (the server's started_at minus the measured offset). */
export function startedAtMs(g: Gift, offsetMs: number): number {
  const t = g.effect ? Date.parse(g.effect.started_at) : NaN;
  return Number.isNaN(t) ? Date.now() : t - offsetMs;
}

/** Still running at `now` (client clock)? */
export function effectLive(g: Gift, offsetMs: number, now = Date.now()): boolean {
  if (!g.effect) return false;
  return startedAtMs(g, offsetMs) + g.effect.duration_ms > now;
}

/** Preset Polaroid caption: one of the fx module's, or the date. */
export function pickCaption(captions: readonly string[], date: Date, rand: () => number = Math.random): string {
  const options = [...captions, date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })];
  return options[Math.floor(rand() * options.length)] ?? options[0]!;
}

export const COUNTDOWN_MS = 3000;
